"use client";

import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Card, CardContent } from "../../../card";

interface ChartStackedBarProps {
  data: {
    title?: string;
    xAxisKey: string;
    bars: Array<{ key: string; color: string; name?: string }>;
    dataset: Array<Record<string, any>>;
  };
}

// Dub.co brand colors approximation for fallback
const COLORS = {
  blue: "#3b82f6",
  violet: "#7c3aed",
  teal: "#2dd4bf",
  neutral: "#e5e5e5",
  grid: "#f5f5f5",
  text: "#737373"
};

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="rounded-lg border border-neutral-200 bg-white p-3 shadow-sm">
        <p className="mb-2 text-sm font-medium text-neutral-900">{label}</p>
        <div className="space-y-1">
          {payload.map((entry: any, index: number) => (
            <div key={index} className="flex items-center gap-2 text-xs text-neutral-600">
              <div 
                className="h-2 w-2 rounded-sm" 
                style={{ backgroundColor: entry.color }}
              />
              <span className="capitalize">{entry.name}:</span>
              <span className="font-medium text-neutral-900">
                {typeof entry.value === 'number' 
                  ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumSignificantDigits: 3 }).format(entry.value) 
                  : entry.value}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  }
  return null;
};

export const ChartStackedBar = ({ data }: ChartStackedBarProps) => {
  if (!data?.dataset || !data?.bars || !Array.isArray(data.bars) || !Array.isArray(data.dataset)) return null;
  
  return (
    <Card className="my-8">
      <CardContent className="p-6">
        {data.title && (
          <div className="mb-6 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-neutral-900">{data.title}</h3>
          </div>
        )}
        <div className="h-[400px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={data.dataset}
              margin={{
                top: 20,
                right: 0,
                left: -20, // Pull left to align with edge if needed, or keeping standard margin
                bottom: 0,
              }}
            >
              <CartesianGrid 
                strokeDasharray="3 3" 
                vertical={false} 
                stroke={COLORS.neutral} 
              />
              <XAxis 
                dataKey={data.xAxisKey} 
                axisLine={false} 
                tickLine={false} 
                tick={{ fill: COLORS.text, fontSize: 12 }}
                dy={10}
              />
              <YAxis 
                axisLine={false} 
                tickLine={false} 
                tick={{ fill: COLORS.text, fontSize: 12 }}
                tickFormatter={(value) => 
                  new Intl.NumberFormat('en-US', { notation: "compact", compactDisplay: "short" }).format(value)
                }
              />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: COLORS.grid }} />
              <Legend 
                wrapperStyle={{ paddingTop: '20px' }} 
                formatter={(value) => <span className="ml-1 text-sm font-medium text-neutral-600">{value}</span>}
              />
              {data.bars.map((bar, index) => (
                <Bar 
                  key={bar.key} 
                  dataKey={bar.key} 
                  stackId="a" 
                  fill={bar.color} 
                  name={bar.name || bar.key}
                  radius={index === data.bars.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                  maxBarSize={60}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
};
